import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWebhookEndpoint,
  UpdateWebhookEndpoint,
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookEndpoint,
  WebhookEndpointCreated,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';

/**
 * Webhook endpoints + their delivery log (WS6 developer panel) over the WS1 webhooks module.
 * Owner/manager only (mirrors the API requireManager + requireScope('webhooks:manage') guards);
 * the panel gates the whole surface before the server ever returns a 403.
 *
 * The signing secret is returned ONCE — by create and by a rotate-secret update (both as
 * WebhookEndpointCreated). The list/get responses never carry it, so the panel surfaces the secret
 * inline at the moment it's minted and never again.
 *
 * Query keys live here (not in the shared lib/query.ts) so the developer panel owns its own cache
 * namespace; nothing else in the console reads webhooks.
 */
const wk = {
  endpoints: ['webhooks'] as const,
  deliveries: (endpointId: string, status?: WebhookDeliveryStatus) =>
    ['webhook-deliveries', endpointId, status ?? 'all'] as const,
};

export function useWebhooks() {
  return useQuery({
    queryKey: wk.endpoints,
    queryFn: async (): Promise<WebhookEndpoint[]> => {
      const page = unwrap(await api.GET('/v1/webhooks', { params: { query: { limit: 100 } } }));
      return page.data;
    },
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWebhookEndpoint): Promise<WebhookEndpointCreated> =>
      unwrap(await api.POST('/v1/webhooks', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: wk.endpoints }),
  });
}

/**
 * Update url/events/active, or rotate the signing secret. The response is a union: a rotate carries
 * the new `secret` (WebhookEndpointCreated), a plain update does not (WebhookEndpoint). Callers that
 * rotate should narrow on `'secret' in result` to show the one-time value.
 */
export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      body: UpdateWebhookEndpoint;
    }): Promise<WebhookEndpoint | WebhookEndpointCreated> =>
      unwrap(
        await api.PATCH('/v1/webhooks/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: wk.endpoints }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/webhooks/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: wk.endpoints }),
  });
}

/** Delivery log for one endpoint, newest first. `enabled` lets the caller defer until expanded. */
export function useWebhookDeliveries(
  endpointId: string | undefined,
  status: WebhookDeliveryStatus | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: wk.deliveries(endpointId ?? '', status),
    enabled: Boolean(endpointId) && enabled,
    queryFn: async (): Promise<WebhookDelivery[]> => {
      const page = unwrap(
        await api.GET('/v1/webhooks/{id}/deliveries', {
          params: {
            path: { id: endpointId! },
            query: { limit: 50, ...(status ? { status } : {}) },
          },
        }),
      );
      return page.data;
    },
  });
}

/** Re-enqueue a single failed/dead delivery (resets attempts, schedules immediately). */
export function useRedeliverWebhook(endpointId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deliveryId: string): Promise<WebhookDelivery> =>
      unwrap(
        await api.POST('/v1/webhooks/{id}/deliveries/{deliveryId}/redeliver', {
          params: { path: { id: endpointId, deliveryId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['webhook-deliveries', endpointId] });
      void qc.invalidateQueries({ queryKey: wk.endpoints });
    },
  });
}

/** Send a synthetic ping to verify an endpoint is reachable; appears in the delivery log. */
export function useTestWebhook(endpointId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<WebhookDelivery> =>
      unwrap(await api.POST('/v1/webhooks/{id}/test', { params: { path: { id: endpointId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['webhook-deliveries', endpointId] });
      void qc.invalidateQueries({ queryKey: wk.endpoints });
    },
  });
}
